// Mocks for /api/secure/me/vitals/*

import { delay } from './index'

const STORAGE_KEY = 'patientPortal.mock.vitals.v1'

function readStore() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
  } catch (e) {
    return {}
  }
}
function writeStore(s) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
  } catch (e) {
    /* ignore */
  }
}

function seedFor(profile) {
  if (!profile) return { entries: [] }
  const isSelf = profile.relation === 'SELF'
  const now = Date.now()
  const day = 24 * 60 * 60 * 1000
  const entries = []
  if (isSelf) {
    // 14 days of mood + sleep
    for (let i = 14; i >= 1; i--) {
      entries.push({
        id: `mood_${i}`,
        metric: 'MOOD',
        value: 3 + Math.round(Math.random()),
        unit: '1-5',
        takenAt: new Date(now - i * day).toISOString(),
      })
      entries.push({
        id: `sleep_${i}`,
        metric: 'SLEEP_HOURS',
        value: 6 + Math.round(Math.random() * 2 * 10) / 10,
        unit: 'hours',
        takenAt: new Date(now - i * day - 3600 * 1000).toISOString(),
      })
    }
  } else {
    // BP + weight history for chronic-condition profile
    for (let i = 14; i >= 1; i--) {
      const sys = 125 + Math.round(Math.random() * 15)
      const dia = 78 + Math.round(Math.random() * 8)
      entries.push({
        id: `bp_${i}`,
        metric: 'BP',
        value: sys,
        valueText: `${sys}/${dia}`,
        unit: 'mmHg',
        takenAt: new Date(now - i * day).toISOString(),
      })
      if (i % 3 === 0) {
        entries.push({
          id: `wt_${i}`,
          metric: 'WEIGHT',
          value: 68 + Math.round(Math.random() * 10) / 10,
          unit: 'kg',
          takenAt: new Date(now - i * day).toISOString(),
        })
      }
    }
  }
  return { entries }
}

function ensure(profile) {
  const store = readStore()
  if (!store[profile.id]) {
    store[profile.id] = seedFor(profile)
    writeStore(store)
  }
  return { store, prof: store[profile.id] }
}

export async function list(profile, metric, from, to) {
  await delay()
  if (!profile) return []
  const { prof } = ensure(profile)
  const fromMs = from ? Date.parse(from) : 0
  const toMs = to ? Date.parse(to) : Number.POSITIVE_INFINITY
  return prof.entries
    .filter((e) => !metric || e.metric === metric)
    .filter((e) => {
      const t = Date.parse(e.takenAt || 0)
      return t >= fromMs && t < toMs
    })
    .sort((a, b) => (a.takenAt || '').localeCompare(b.takenAt || ''))
}

export async function record(profile, body) {
  await delay()
  if (!profile) return null
  const { store, prof } = ensure(profile)
  const entry = {
    id: `vital_${Date.now()}`,
    metric: body.metric,
    value: body.value ?? null,
    valueText: body.valueText || null,
    unit: body.unit || null,
    takenAt: body.takenAt || new Date().toISOString(),
    note: body.note || null,
  }
  prof.entries.push(entry)
  store[profile.id] = prof
  writeStore(store)
  return entry
}

export async function requiredToday(profile) {
  await delay(80)
  if (!profile) return []
  const isSelf = profile.relation === 'SELF'
  const baseline = isSelf
    ? [{ metric: 'MOOD', label: 'Mood check-in' }]
    : [
        { metric: 'BP', label: 'Blood pressure (morning)' },
        { metric: 'WEIGHT', label: 'Weight' },
      ]
  const todayStr = new Date().toISOString().slice(0, 10)
  const { prof } = ensure(profile)
  return baseline.map((b) => ({
    ...b,
    loggedToday: prof.entries.some(
      (e) => e.metric === b.metric && (e.takenAt || '').slice(0, 10) === todayStr,
    ),
  }))
}

export function __resetVitalsMock() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    /* ignore */
  }
}
